import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar, FileText, Users, Shield, TrendingUp, Stethoscope, CreditCard, Share2, Heart, Mail, Phone, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import logo from '@/assets/logo.png';

export const Landing = () => {
  const [contactForm, setContactForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [contactLoading, setContactLoading] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    try {
      await api.post('/contact', contactForm);
      toast.success('Message sent! We\'ll get back to you soon.');
      setContactForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setContactLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-md z-50 border-b border-sage-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img src={logo} alt="VetCare" className="h-8 w-auto" />
            <span className="text-2xl font-heading font-bold text-sage-700">VetCare</span>
          </div>
          <div className="flex gap-4">
            <Link to="/login">
              <Button variant="ghost" className="rounded-full" data-testid="nav-login-button">
                Login
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-sage-500 hover:bg-sage-600 text-white rounded-full px-6 shadow-lg shadow-sage-500/30 hover:-translate-y-0.5 transition-all" data-testid="nav-register-button">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl md:text-7xl font-heading font-extrabold tracking-tight text-sage-800 mb-6">
                Vet Care <span className="text-terracotta-500">Reimagined</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed">
                Manage your veterinary clinic with warmth and efficiency. Track pets, owners, visits, medical records, and diagnoses—all in one beautiful platform.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/register">
                  <Button className="bg-sage-500 hover:bg-sage-600 text-white rounded-full px-8 py-6 text-lg shadow-xl shadow-sage-500/30 hover:-translate-y-1 transition-all" data-testid="hero-get-started-button">
                    Start Free Trial
                  </Button>
                </Link>
                <a href="#features">
                  <Button variant="outline" className="rounded-full px-8 py-6 text-lg border-sage-300 hover:bg-sage-50" data-testid="hero-learn-more-button">
                    Learn More
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-sage-100 rounded-[3rem] transform rotate-6"></div>
              <img
                src="https://images.unsplash.com/photo-1637708847394-1949f0f06c2b?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
                alt="Veterinarian with dog"
                className="relative z-10 rounded-[3rem] shadow-2xl w-full h-auto"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-sage-800 mb-4">
              Everything Your Clinic Needs
            </h2>
            <p className="text-lg text-gray-600">Powerful tools designed for modern veterinary care</p>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
            {[
              { icon: Users, title: 'Owner Management', desc: 'Keep detailed records of pet owners with easy search and filtering' },
              { icon: Heart, title: 'Pet Care Tracking', desc: 'Monitor pet health, appointments, and medical history with cloud image uploads' },
              { icon: Calendar, title: 'Smart Scheduling', desc: 'Calendar and list views for efficient appointment management with one-click visit conversion' },
              { icon: Stethoscope, title: 'Visit & Medical Records', desc: 'Complete visit lifecycle with diagnoses, treatments, prescriptions, and vaccination tracking' },
              { icon: FileText, title: 'Services & Billing', desc: 'Service catalog with pricing linked to visits for automatic billing and invoicing' },
              { icon: CreditCard, title: 'Stripe Payments', desc: 'Seamless subscription upgrades with secure Stripe checkout and payment verification' },
              { icon: Share2, title: 'Referral Program', desc: 'Earn referral points by inviting other clinics and unlock plan upgrades for free' },
              { icon: Shield, title: 'Multi-Tenant Security', desc: 'Complete data isolation for each clinic with role-based access and Google OAuth' },
              { icon: TrendingUp, title: 'Analytics Dashboard', desc: 'Real-time insights into your clinic operations backed by Redis-powered caching' }
            ].map((feature, idx) => (
              <div key={idx} className="bg-sage-50 rounded-3xl p-8 hover:shadow-xl hover:scale-105 transition-all duration-300" data-testid={`feature-card-${idx}`}>
                <feature.icon className="w-12 h-12 text-sage-600 mb-4" />
                <h3 className="text-2xl font-heading font-semibold text-sage-800 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-sage-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-sage-800 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-gray-600">Start free, upgrade when you're ready</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-3xl p-8 shadow-lg" data-testid="pricing-free-plan">
              <h3 className="text-2xl font-heading font-bold text-sage-800 mb-2">Free Plan</h3>
              <p className="text-4xl font-bold text-sage-600 mb-6">$0<span className="text-lg text-gray-500">/month</span></p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2"><span className="text-sage-500">✓</span> Up to 50 owners</li>
                <li className="flex items-center gap-2"><span className="text-sage-500">✓</span> 2 pets per owner</li>
                <li className="flex items-center gap-2"><span className="text-sage-500">✓</span> 2 doctors</li>
                <li className="flex items-center gap-2"><span className="text-sage-500">✓</span> 5 pet types</li>
                <li className="flex items-center gap-2"><span className="text-sage-500">✓</span> Full visit & medical records</li>
              </ul>
              <Link to="/register">
                <Button className="w-full rounded-full py-6 bg-sage-500 hover:bg-sage-600 text-white" data-testid="free-plan-button">
                  Get Started Free
                </Button>
              </Link>
            </div>
            <div className="bg-gradient-to-br from-sage-500 to-sage-700 rounded-3xl p-8 shadow-2xl relative ring-4 ring-sage-300" data-testid="pricing-basic-plan">
              <div className="absolute -top-4 right-8 bg-terracotta-500 text-white px-4 py-1 rounded-full text-sm font-bold">Best Value</div>
              <h3 className="text-2xl font-heading font-bold text-white mb-2">Basic Plan</h3>
              <p className="text-4xl font-bold text-white mb-6">$19.99<span className="text-lg text-sage-100">/month</span></p>
              <ul className="space-y-3 mb-8 text-white">
                <li className="flex items-center gap-2"><span>✓</span> Up to 200 owners</li>
                <li className="flex items-center gap-2"><span>✓</span> 5 pets per owner</li>
                <li className="flex items-center gap-2"><span>✓</span> 5 doctors</li>
                <li className="flex items-center gap-2"><span>✓</span> Unlimited pet types</li>
                <li className="flex items-center gap-2"><span>✓</span> Full visit & medical records</li>
                <li className="flex items-center gap-2"><span>✓</span> Stripe-powered billing</li>
                <li className="flex items-center gap-2"><span>✓</span> Priority support</li>
              </ul>
              <Link to="/register">
                <Button className="w-full rounded-full py-6 bg-white hover:bg-sage-50 text-sage-700" data-testid="basic-plan-button">
                  Start Basic Plan
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-sage-800 mb-4">
            What Clinics Are Saying
          </h2>
          <div className="grid md:grid-cols-2 gap-8 mt-12">
            {[
              { name: 'Dr. Sarah Mitchell', clinic: 'Paws & Claws Veterinary', quote: 'VetCare has transformed how we manage our clinic. The visit records and prescription tracking save us so much time!' },
              { name: 'Dr. James Chen', clinic: 'City Pet Hospital', quote: 'The scheduling, visit management, and Stripe billing features save us hours every week. The referral program is a great bonus!' }
            ].map((testimonial, idx) => (
              <div key={idx} className="bg-sage-50 rounded-3xl p-8" data-testid={`testimonial-${idx}`}>
                <p className="text-lg text-gray-700 mb-4 italic">"{testimonial.quote}"</p>
                <p className="font-heading font-semibold text-sage-800">{testimonial.name}</p>
                <p className="text-sm text-gray-600">{testimonial.clinic}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Us Section */}
      <section id="contact" className="py-20 px-6 bg-sage-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-sage-800 mb-4">
              Get In Touch
            </h2>
            <p className="text-lg text-gray-600">Have questions? We'd love to hear from you.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Contact info */}
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-sage-100 rounded-2xl">
                  <Mail className="w-6 h-6 text-sage-600" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-sage-800 mb-1">Email Us</h3>
                  <p className="text-gray-600">support@vetcare.app</p>
                  <p className="text-sm text-gray-500 mt-1">We typically reply within 24 hours.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-3 bg-sage-100 rounded-2xl">
                  <Phone className="w-6 h-6 text-sage-600" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-sage-800 mb-1">Call Us</h3>
                  <p className="text-gray-600">+1 (800) 838-2273</p>
                  <p className="text-sm text-gray-500 mt-1">Mon–Fri, 9am–6pm EST</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="p-3 bg-sage-100 rounded-2xl">
                  <MapPin className="w-6 h-6 text-sage-600" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-sage-800 mb-1">Office</h3>
                  <p className="text-gray-600">123 Veterinary Lane, Suite 100</p>
                  <p className="text-gray-600">New York, NY 10001</p>
                </div>
              </div>
            </div>

            {/* Contact form */}
            <form onSubmit={handleContactSubmit} className="bg-white rounded-3xl p-8 shadow-lg space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact-name">Your Name</Label>
                  <Input
                    id="contact-name"
                    placeholder="Jane Smith"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    required
                    maxLength={100}
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-email">Email Address</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    placeholder="jane@clinic.com"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    required
                    className="mt-1 rounded-xl"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="contact-subject">Subject</Label>
                <Input
                  id="contact-subject"
                  placeholder="How can we help?"
                  value={contactForm.subject}
                  onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                  required
                  maxLength={200}
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label htmlFor="contact-message">Message</Label>
                <textarea
                  id="contact-message"
                  placeholder="Tell us more about your question or feedback..."
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  required
                  maxLength={2000}
                  rows={5}
                  className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                />
              </div>
              <Button
                type="submit"
                disabled={contactLoading}
                className="w-full bg-sage-500 hover:bg-sage-600 text-white rounded-xl py-5 text-base"
              >
                {contactLoading ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </div>
        </div>
      </section>

      <footer className="bg-sage-800 text-white py-12 px-6">
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img src={logo} alt="VetCare" className="h-6 w-auto" />
              <span className="text-xl font-heading font-bold">VetCare</span>
            </div>
            <p className="text-sage-200">Modern veterinary management for caring clinics.</p>
          </div>
          <div>
            <h4 className="font-heading font-semibold mb-4">Product</h4>
            <ul className="space-y-2 text-sage-200">
              <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
              <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
              <li><a href="#contact" className="hover:text-white transition-colors">Contact Us</a></li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading font-semibold mb-4">Legal</h4>
            <ul className="space-y-2 text-sage-200">
              <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
              <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-8 pt-8 border-t border-sage-700 text-center text-sage-300">
          <p>&copy; 2026 VetCare. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};
